/**
 * RbacContext — runtime RBAC manifest for the HHMS MIS frontend.
 *
 * What it does
 * ────────────
 * 1. Fetches GET /rbac/manifest on login / mount, giving us:
 *      - nav[]      — sidebar navigation items the user may see
 *      - routes[]   — guarded React routes
 *      - granted[]  — flat list of permission keys the user holds
 *      - version    — monotonic integer; increases on any RBAC change
 *
 * 2. Polls GET /rbac/version every 30 s (SSE stream optional future upgrade).
 *    When the version changes, re-fetches the manifest automatically.
 *
 * 3. Exposes:
 *      can(permissionKey)  → boolean
 *      nav                 → [{group_label, group_order, items:[{...}]}]
 *      routes              → [{route, component, permission_key, is_public}]
 *      granted             → string[]
 *      version             → number
 *      loading             → boolean
 *      error               → string | null
 *      reload()            → force re-fetch
 *
 * Usage
 * ─────
 *   // Wrap app:
 *   <RbacProvider><App /></RbacProvider>
 *
 *   // In any component:
 *   const { can } = useRbac();
 *   if (can('page.billing')) { ... }
 *
 *   // Permission-aware nav is already filtered:
 *   const { nav } = useRbac();
 *
 * Fallback
 * ────────
 * If the manifest cannot be fetched (network error, auth not ready), `can()`
 * returns false for every permission (fail-closed), and `nav` is an empty array.
 * The local hard-coded PERMISSIONS map (utils/permissions.js) is still used
 * by RequireRole / Can as a secondary check for backward-compatibility.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import api from '../services/api';
import { useAuth } from './AuthContext';

// ── Context ──────────────────────────────────────────────────────────────────

const RbacContext = createContext({
  can:     () => false,
  nav:     [],
  routes:  [],
  granted: [],
  version: 0,
  loading: true,
  error:   null,
  reload:  () => {},
});

export const useRbac = () => useContext(RbacContext);

// ── Helpers ──────────────────────────────────────────────────────────────────

/**
 * Group flat nav rows (from /rbac/manifest) by group so the sidebar gets:
 *   [ { group_label, group_order, items: [{label, route, icon, order, permission}] }, ... ]
 *
 * Manifest nav item shape:
 *   { group, group_order, label, order, route, icon, permission }
 */
function groupNav(rows) {
  if (!rows || !Array.isArray(rows)) return [];
  const map = new Map();
  rows.forEach((item) => {
    const key = item.group || item.group_label || '';
    if (!map.has(key)) {
      map.set(key, {
        group_label: key,
        group_order: item.group_order ?? 0,
        items: [],
      });
    }
    map.get(key).items.push(item);
  });
  const groups = Array.from(map.values());
  groups.sort((a, b) => a.group_order - b.group_order);
  groups.forEach((g) =>
    g.items.sort((a, b) => (a.order ?? a.item_order ?? 0) - (b.order ?? b.item_order ?? 0))
  );
  return groups;
}

// ── Provider ─────────────────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000;

export const RbacProvider = ({ children }) => {
  const [manifest, setManifest] = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);

  const versionRef = useRef(0);
  const pollTimer  = useRef(null);

  // The manifest is PER-USER: it must be refetched whenever the logged-in
  // identity changes (login/logout/account switch), not only when the RBAC
  // version bumps. Without this, switching accounts in-app showed the
  // previous user's navigation (e.g. a patient seeing the admin sidebar).
  const { user } = useAuth();
  const userKey = user?.party_id || user?.username || null;

  // ── Fetch manifest ────────────────────────────────────────────────────────
  const fetchManifest = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      // Not logged in yet — leave in loading=false with empty manifest
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/rbac/manifest');
      const m = data?.data ?? data;   // unwrap envelope if present
      versionRef.current = m?.version ?? 0;
      setManifest(m);
      setError(null);
    } catch (err) {
      const msg = err?.response?.status === 401
        ? null   // not logged in — silent
        : `RBAC manifest unavailable (${err?.response?.status ?? 'network error'})`;
      setError(msg);
      setManifest(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Poll for version changes ──────────────────────────────────────────────
  const pollVersion = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) return;
    try {
      const { data } = await api.get('/rbac/version');
      const v = data?.data?.version ?? data?.version ?? 0;
      if (v !== versionRef.current) {
        versionRef.current = v;
        await fetchManifest();
      }
    } catch {
      // Network blip — ignore
    }
  }, [fetchManifest]);

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!userKey) {
      // Logged out — drop the previous user's manifest immediately so no
      // stale (potentially more-privileged) nav survives an account switch.
      setManifest(null);
      versionRef.current = 0;
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchManifest();

    pollTimer.current = setInterval(pollVersion, POLL_INTERVAL_MS);
    return () => clearInterval(pollTimer.current);
  }, [userKey, fetchManifest, pollVersion]);

  // ── Derived values ────────────────────────────────────────────────────────
  const grantedSet = new Set(manifest?.granted ?? []);
  const nav        = groupNav(manifest?.nav);
  const routes     = manifest?.routes ?? [];
  const version    = manifest?.version ?? 0;

  /**
   * can(permissionKey) → boolean
   * Primary RBAC check — reads from the live manifest.
   * Falls back to false (fail-closed) if the manifest hasn't loaded yet.
   */
  const can = useCallback(
    (permissionKey) => {
      if (!permissionKey) return true;   // public item — always visible
      if (!manifest)       return false;  // fail-closed while loading
      return grantedSet.has(permissionKey);
    },
    [manifest], // grantedSet is derived from manifest — listing manifest is enough
  );

  const reload = useCallback(() => {
    setLoading(true);
    fetchManifest();
  }, [fetchManifest]);

  return (
    <RbacContext.Provider value={{ can, nav, routes, granted: manifest?.granted ?? [], version, loading, error, reload }}>
      {children}
    </RbacContext.Provider>
  );
};

export default RbacContext;
